import { subDays, subMonths, subWeeks } from 'date-fns'

import {
  REQUEST_COMPLETED_STATUS,
  REQUEST_DENIED_STATUS,
  REQUEST_IN_PROGRESS_STATUS,
  REQUEST_PENDING_STATUS,
} from '../../constants/request'
import { FileIconType } from '../FileIcon'

import {
  BirdviewHomeContactType,
  DashboardTileTone,
} from './DashboardBirdview.constants'
import {
  BirdviewAnalyticsPreset,
  BirdviewRequestChart,
} from './DashboardBirdview.constants.charts'

import BuildingIcon from '../../icons/building.svg'
import EmailIcon from '../../icons/email.svg'
import MapPinIcon from '../../icons/map-pin.svg'
import PhoneIcon from '../../icons/phone.svg'
import ServiceRequestIcon from '../../icons/service-request.svg'

const BIRDVIEW_MOCK_NOW = new Date()

const BIRDVIEW_FLOW_MOCK_DAYS = 90

function getBirdviewFlowMockPoint(index) {
  const opened = Math.round(14 + 6 * Math.sin(index / 3) + index / 9)
  const closed = Math.max(3, Math.round(opened - 3 - 3 * Math.cos(index / 4)))

  return {
    closed,
    date: subDays(
      BIRDVIEW_MOCK_NOW,
      BIRDVIEW_FLOW_MOCK_DAYS - 1 - index
    ).toISOString(),
    opened,
  }
}

export const BIRDVIEW_KPIS_MOCK = {
  [BirdviewAnalyticsPreset.Personal]: {
    myAvgResolution: {
      delta: -0.2,
      spark: [2.4, 2.3, 2.2, 2.1, 2, 2, 1.9, 1.9],
      tone: DashboardTileTone.Success,
      value: 1.9,
    },
    resolvedThisWeek: {
      delta: 6,
      spark: [38, 40, 39, 42, 44, 45, 47, 48],
      tone: DashboardTileTone.Success,
      value: 48,
    },
    onTime: {
      delta: -3,
      spark: [93, 92, 92, 90, 89, 88, 87, 88],
      tone: DashboardTileTone.Error,
      value: 88,
    },
  },
  [BirdviewAnalyticsPreset.Portfolio]: {
    avgResolution: {
      delta: -0.3,
      spark: [3.1, 2.9, 3, 2.7, 2.6, 2.5, 2.4, 2.4],
      tone: DashboardTileTone.Success,
      value: 2.4,
    },
    backlog: {
      delta: 11,
      spark: [30, 32, 31, 34, 36, 38, 40, 41],
      tone: DashboardTileTone.Error,
      value: 41,
    },
    onTime: {
      delta: -3,
      spark: [92, 91, 90, 89, 88, 87, 86, 86],
      tone: DashboardTileTone.Error,
      value: 86,
    },
  },
}

export const BIRDVIEW_REQUEST_CHARTS_MOCK = {
  [BirdviewRequestChart.Aging]: {
    buckets: {
      days7To13: 28,
      days14To29: 30,
      days30To89: 22,
      days90Plus: 9,
    },
    openTotal: 123,
  },
  [BirdviewRequestChart.ByBuilding]: [
    {
      avgDays: 6.4,
      id: 'building-1',
      label: 'Kaiserin-Elisabeth-Straße 145, Stiege 3',
      units: 146,
      value: 37,
    },
    {
      avgDays: 3.1,
      id: 'building-2',
      label: 'Rosenweg 8',
      units: 92,
      value: 28,
    },
    {
      avgDays: 4.8,
      id: 'building-3',
      label: 'Lindenweg 3',
      units: 48,
      value: 22,
    },
    {
      avgDays: 2.6,
      id: 'building-4',
      label: 'Ahornstraße 5',
      units: 61,
      value: 14,
    },
    {
      avgDays: 3.9,
      id: 'building-5',
      labelKey: 'DashboardBirdview.ChartOtherKey',
      units: 24,
      value: 4,
    },
  ],
  [BirdviewRequestChart.ByManager]: {
    items: [
      {
        avgDays: 3.1,
        id: 'manager-1',
        label: 'Anna Becker',
        open: 30,
        overdue: 4,
      },
      {
        avgDays: 2.4,
        id: 'manager-2',
        label: 'Thomas Gruber',
        open: 23,
        overdue: 3,
      },
      {
        avgDays: 2.9,
        id: 'manager-3',
        label: 'Sandra Hofer',
        open: 19,
        overdue: 2,
      },
      {
        avgDays: 4.2,
        id: 'manager-4',
        label: 'Michael Wagner',
        open: 10,
        overdue: 5,
      },
      {
        avgDays: 6.8,
        id: 'manager-5',
        labelKey: 'DashboardBirdview.TileUnassignedKey',
        open: 3,
        overdue: 6,
      },
    ],
    openTotal: 92,
  },
  [BirdviewRequestChart.ByStatus]: {
    [REQUEST_COMPLETED_STATUS]: 60,
    [REQUEST_DENIED_STATUS]: 12,
    [REQUEST_IN_PROGRESS_STATUS]: 48,
    [REQUEST_PENDING_STATUS]: 80,
  },
  [BirdviewRequestChart.ByType]: [
    { id: 'type-1', label: 'Heating', value: 37 },
    { id: 'type-2', label: 'Water', value: 27 },
    { id: 'type-3', label: 'Electrical', value: 20 },
    { id: 'type-4', label: 'Keys/Access', value: 13 },
    { id: 'type-5', labelKey: 'DashboardBirdview.ChartOtherKey', value: 8 },
  ],
  [BirdviewRequestChart.OpenedVsClosed]: Array.from(
    { length: BIRDVIEW_FLOW_MOCK_DAYS },
    (_, index) => getBirdviewFlowMockPoint(index)
  ),
}

export const BIRDVIEW_GRETA_SECTION_MOCK = {
  items: [
    {
      Icon: ServiceRequestIcon,
      id: 'greta-1',
      title: 'Assign Elektro Schmidt → “Heating broken — Bergstraße 16”',
      why: 'why: nearest free provider',
    },
    {
      Icon: EmailIcon,
      id: 'greta-2',
      title: 'Draft reply to Ms Müller — “technician booked Thu 9:00”',
      why: 'why: slot confirmed',
    },
    {
      Icon: ServiceRequestIcon,
      id: 'greta-3',
      title: 'Escalate “Water-damage estimate” — stalled',
      why: 'why: no update 48h',
    },
    {
      Icon: ServiceRequestIcon,
      id: 'greta-4',
      title: 'Tag “Lift defect — Rosenweg 8” as urgent',
      why: 'why: safety category',
    },
  ],
  total: 4,
}

export const BIRDVIEW_DWELLER_HOME_SECTION_MOCK = {
  items: [
    {
      Icon: MapPinIcon,
      id: 'home-1',
      labelKey: 'DashboardBirdview.HomeAddressLabelKey',
      value: 'Bergstraße 16 · Flat 12',
    },
    {
      Icon: BuildingIcon,
      id: 'home-2',
      labelKey: 'DashboardBirdview.HomePropertyManagementLabelKey',
      value: 'Hausverwaltung Sonnberg',
    },
    {
      Icon: PhoneIcon,
      id: 'home-3',
      labelKey: 'DashboardBirdview.HomePhoneLabelKey',
      note: 'Mon–Fri, 09:00–16:00',
      type: BirdviewHomeContactType.Phone,
      value: '+43 1 234 56 78',
    },
    {
      Icon: EmailIcon,
      id: 'home-4',
      labelKey: 'DashboardBirdview.HomeEmailLabelKey',
      type: BirdviewHomeContactType.Email,
      value: 'office@hv-sonnberg.at',
    },
    {
      Icon: PhoneIcon,
      id: 'home-5',
      labelKey: 'DashboardBirdview.HomeEmergencyPhoneLabelKey',
      note: '24/7',
      type: BirdviewHomeContactType.Phone,
      value: '+43 800 123 456',
    },
  ],
  total: 5,
}

export const BIRDVIEW_DWELLER_DOCUMENTS_SECTION_MOCK = {
  items: [
    {
      address: 'Flat 12 · 2025',
      fileType: FileIconType.Xls,
      id: 'dweller-document-1',
      title: 'Operating cost statement 2025 (Betriebskostenabrechnung)',
      updated: subDays(BIRDVIEW_MOCK_NOW, 2).toISOString(),
    },
    {
      address: 'Bergstraße 16',
      fileType: FileIconType.Pdf,
      id: 'dweller-document-2',
      title: 'House rules (Hausordnung)',
      updated: subWeeks(BIRDVIEW_MOCK_NOW, 2).toISOString(),
    },
    {
      address: 'Flat 12',
      fileType: FileIconType.Doc,
      id: 'dweller-document-3',
      title: 'Rental agreement (Mietvertrag)',
      updated: subMonths(BIRDVIEW_MOCK_NOW, 1).toISOString(),
    },
  ],
  total: 3,
}
